// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { firebaseAuth } from '../config/firebase'; // Import initialized Firebase Auth
import logger from '../utils/logger'; // Assuming you have a logger

export interface UserWorkspaceRole {
  id: string; // logical record id
  workspaceId: string;
  roleId: string;
}

// Define the shape of your user data (combine Firebase info and your profile data)
export interface AppUser {
  uid: string; // Firebase UID
  firebaseUser?: FirebaseUser; // Keep the original Firebase user object
  // Add fields fetched from your /users/me endpoint
  name?: string;
  email?: string;
  avatarUrl?: string;
  authProvider?: string;
  profileStatus?: string;
  workspaces: UserWorkspaceRole[]; // Define a proper type for workspaces
  // Add other profile fields as needed
}

interface AuthContextType {
  user: AppUser | null;
  setUser: React.Dispatch<React.SetStateAction<AppUser | null>>; // Allow setting profile data
  loading: boolean;
  isSignedIn: boolean;
  getIdToken: () => Promise<string | null>; // Function to get the token

  signIn: (userData: AppUser) => void;
  // Add signOut method if not already present
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true); // Start loading until auth state is known

  useEffect(() => {
    // Listen for Firebase authentication state changes
    const unsubscribe = onAuthStateChanged( firebaseAuth, (firebaseUser) => {
      if (firebaseUser) {
        logger.info(`Auth state changed: User signed in (UID: ${firebaseUser.uid})`);
        // logger.info( firebaseUser);
        // User is signed in. Store basic info initially.
        // The profile data will be fetched separately (e.g., by MyProfileComponent or here)
        const appUser: AppUser = {
          uid: firebaseUser.uid,
          firebaseUser: firebaseUser,
          // Initialize other fields as undefined/null until fetched
          // use email if there is no displayName present
          name: firebaseUser.displayName ?? (firebaseUser.email ?? "N/A"),
          email: firebaseUser.email ?? "",
          avatarUrl: firebaseUser.photoURL ?? undefined,
          authProvider: "Firebase AuthN",
          profileStatus: "partial",
          workspaces: []
        };
        // logger.info(appUser);
        setUser( appUser);
      } else {
        logger.info("Auth state changed: User signed out.");
        // User is signed out
        setUser(null);
      }
      setLoading(false); // Auth state is now known
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []); // Run only once on mount

  // Function to get the current ID token
  const getIdToken = async (): Promise<string | null> => {
    if (user?.firebaseUser) {
      try {
        // Force refresh if token is expired (optional, getIdToken handles caching)
        // const token = await user.firebaseUser.getIdToken(true);
        const token = await user.firebaseUser.getIdToken();
        return token;
      } catch (error) {
        logger.error("Error getting ID token:", error);
        return null;
      }
    }
    return null;
  };

  const signIn = (userData: any) => {
    // setIsSignedIn(true);
    const appUser: AppUser = {
      
      uid: userData.id,
      firebaseUser: undefined, // since this is a manually contsructed auth object
      name: userData.name,
      email: userData.email,
      avatarUrl: userData.avatarUrl,
      authProvider: "self",
      workspaces: [] // None concretely available 
    }
    
    setUser(appUser);
    logger.info( `AuthContext:signIn: signed in user is: ${userData.username} with id: ${userData.id}.`);
    // You might also want to store the user data in local storage here
  };

  // Sign out function
  const signOut = async () => {
    try {
      await firebaseAuth.signOut();
      // setUser(null) will be handled by onAuthStateChanged
      logger.info("User signed out successfully.");
    } catch (error) {
      logger.error("Error signing out:", error);
    }
  };


  const value = {
    user,
    setUser, // Expose setUser so components can update profile data
    loading,
    isSignedIn: !!user, // True if user object exists
    getIdToken,
    signIn,
    signOut,
  };

  // Don't render children until authentication state is determined
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
